import { Box } from '@material-ui/core';
import {
  getAgentById,
  getAgentInLenderContextByAgentId,
  getAvailablePricingsByAgentId,
  getLendersByMerchantId,
  getPricingsByAgentId,
} from '@nemuru-eng/js-api-client';
import { getPricingsFromCdnByAgentId } from '@nemuru-eng/js-api-client/slim';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { SWRConfig } from 'swr';

import { PricingSelector } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSelector/PricingSelector';
import { PricingsInCdnDialog } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingsInCdnDialog';
import { UploadPricingsToCdnDialog } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/UploadPricingsToCdnDialog';
import { SelectedAction } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/types/SelectedAction';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { PricingsTable } from '@/components/shared/components/PricingsTable/PricingsTable';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { ModuleCorePermissionProps, PendingDataType, StatusTagType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';
import { useSetPageLoadingWhenRequesting } from '@/hooks/requests/useSetPageLoadingWhenRequesting';

import { EditAgentPricingsRanges } from './components/EditAgentPricingsRanges/EditAgentPricingsRanges';

export const AgentPricings = ({ canEdit = false }: ModuleCorePermissionProps) => {
  const { agentId, merchantId } = useParams();
  if (!agentId || !merchantId) return null;

  const { data: lenders, loading: lendersLoading } = useCache(getLendersByMerchantId, { merchantId });
  const { data: agentPricings, loading: agentPricingsLoading } = useCache(getPricingsByAgentId, { agentId });
  const {
    data: agentAvailablePricings,
    loading: agentAvailablePricingsLoading,
    revalidate: revalidateAgentAvailablePricings,
  } = useCache(getAvailablePricingsByAgentId, { agentId });
  const {
    data: agentPricingsFromCdn,
    loading: agentPricingsFromCdnLoading,
    error,
  } = useCache(getPricingsFromCdnByAgentId, { agentId });
  const { data: agent, loading } = useCache(getAgentById, { agentId });
  const { data: agentInLenderContext, loading: agentInLenderContextLoading } = useCache(
    getAgentInLenderContextByAgentId,
    { agentId },
  );

  const [arePricingsFromCdnFound, setArePricingsFromCdnFound] = useState<boolean | undefined>();

  useEffect(() => {
    if (error) setArePricingsFromCdnFound(false);
    if (agentPricingsFromCdn) setArePricingsFromCdnFound(true);
  }, [agentPricingsFromCdn, error]);

  const { opened, toggleDialog, setOpened } = useDialog();

  const [page, setPage] = useState(0);
  const [selectedAction, setSelectedAction] = useState<SelectedAction>();
  const [openSelector, setOpenSelector] = useState<boolean>(false);

  useSetPageLoadingWhenRequesting('AGENT_IN_LENDER_CONTEXT', [loading, agentInLenderContextLoading, lendersLoading]);
  useSetPageLoadingWhenRequesting('AGENT_PRICINGS', [
    agentPricingsLoading,
    agentAvailablePricingsLoading,
    agentPricingsFromCdnLoading,
  ]);

  const toggleAction = (selectedAction: SelectedAction): void => {
    if (!canEdit) return;
    setSelectedAction(selectedAction);
    toggleDialog();
  };

  const actions: ActionsOption[] = [];
  if (canEdit) {
    actions.push({
      display: <Text tId={'ASSIGN_PRICINGS_TO_AGENT'} />,
      onClick: () => setOpenSelector(true),
      iconName: 'edit',
      disabled: !agentAvailablePricings,
    });
    actions.push({
      display: <Text tId={'UPLOAD_PRICINGS_TO_CDN'} />,
      onClick: () => toggleAction(SelectedAction.UPLOAD_PRICING_TO_CDN),
      iconName: 'cloud_upload',
      disabled: !agentPricings,
    });
    actions.push({
      display: <Text tId={'DOWNLOAD_PRICINGS_FROM_CDN'} />,
      onClick: () => toggleAction(SelectedAction.SEE_PRICING_IN_CDN),
      iconName: 'cloud_download',
      disabled: !agentPricingsFromCdn,
    });
    actions.push({
      display: <Text tId={'EDIT_AGENT_PRICINGS_RANGES'} />,
      onClick: () => toggleAction(SelectedAction.EDIT_AGENT_PRICINGS_RANGES),
      iconName: 'tune',
      disabled: !agentPricings,
    });
  }

  if (!agent || arePricingsFromCdnFound === undefined || !lenders) return null;

  return (
    <SubSectionWrapper
      title={'ASSIGNED_PRICINGS'}
      specificActionsOptionsArray={actions}
      leftComponent={
        <Box mr={1}>
          <StatusTag
            type={arePricingsFromCdnFound ? StatusTagType.SUCCESS : StatusTagType.DEFAULT}
            labelTextId={arePricingsFromCdnFound ? 'UPLOADED_TO_CDN' : 'NOT_UPLOADED_TO_CDN'}
          />
        </Box>
      }
    >
      {agentPricings ? (
        <Box mt={2}>
          <PricingsTable
            pricings={agentPricings}
            lenders={lenders}
            agentInLenderContext={agentInLenderContext}
            page={page}
            setPage={setPage}
            editModeAllowed={canEdit}
            allowedRemovePricingFromAgent
            refreshData={async () => await revalidateAgentAvailablePricings()}
          />
        </Box>
      ) : (
        <PendingDataWarning type={PendingDataType.AGENT_PRICINGS} />
      )}
      <SWRConfig value={{ revalidateOnMount: false }}>
        {opened && agentPricings && selectedAction === SelectedAction.EDIT_AGENT_PRICINGS_RANGES && (
          <EditAgentPricingsRanges
            agentId={agent.id}
            agentPricings={agentPricings}
            opened={opened}
            setOpened={setOpened}
            handleClose={toggleDialog}
          />
        )}
        {agentAvailablePricings && openSelector && (
          <PricingSelector agentId={agent.id} lenders={lenders} open={openSelector} setOpen={setOpenSelector} />
        )}
        {selectedAction === SelectedAction.UPLOAD_PRICING_TO_CDN && (
          <UploadPricingsToCdnDialog
            agentId={agent.id}
            opened={opened}
            setOpened={setOpened}
            handleClose={toggleDialog}
          />
        )}
        {selectedAction === SelectedAction.SEE_PRICING_IN_CDN && agentPricingsFromCdn && (
          <PricingsInCdnDialog
            pricingsFromCdn={agentPricingsFromCdn}
            opened={opened}
            setOpened={setOpened}
            handleClose={toggleDialog}
          />
        )}
      </SWRConfig>
    </SubSectionWrapper>
  );
};
